// @spec AI-STR-022
// AI Insights page — view AI-generated compliance insights, recommendations, and action items
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Lightbulb, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ai/insights')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.aiInsights', 'AI Insights'),
  }),
  component: AIInsightsPage,
});

function AIInsightsPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Lightbulb className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('ai.insightsPageTitle')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('ai.insightsPageDescription')}
              </p>
            </div>
          </div>
        </header>

        {/* Insight categories */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">{t('ai.complianceGaps')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('ai.complianceGapsDescription')}
              </CardDescription>
              <p className="mt-3 text-2xl font-bold text-muted-foreground">—</p>
              <p className="text-xs text-muted-foreground">
                {t('ai.runScanForInsights')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-base">{t('ai.recommendations')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('ai.recommendationsDescription')}
              </CardDescription>
              <p className="mt-3 text-2xl font-bold text-muted-foreground">—</p>
              <p className="text-xs text-muted-foreground">
                {t('ai.recommendationsHint')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <CardTitle className="text-base">{t('ai.actionItems')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('ai.actionItemsDescription')}
              </CardDescription>
              <p className="mt-3 text-2xl font-bold text-muted-foreground">—</p>
              <p className="text-xs text-muted-foreground">
                {t('ai.actionItemsHint')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Empty state */}
        <Card className="flex flex-col items-center justify-center py-12">
          <Lightbulb className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">{t('ai.noInsightsYet')}</p>
          <p className="mb-2 max-w-md text-center text-sm text-muted-foreground">
            AI insights are generated automatically when you run compliance scans, analyze documents,
            or use the AI advisor. Visit the{' '}
            <a href="/ai/scans" className="text-primary hover:underline">
              Compliance Scans
            </a>{' '}
            or{' '}
            <a href="/ai/documents" className="text-primary hover:underline">
              Documents
            </a>{' '}
            page to get started.
          </p>
        </Card>
      </div>
    </div>
  );
}
